import React, { useMemo, useState } from "react";
import Select from "react-select";

import { Button } from "../../../Components/Buttons";
import { formatToTitle, ToastMessage } from "../../../Components/Utils";
import { CustomSelectStyle } from "../../../Components/CustomSelectStyle";
import PermissionsSkeleton from "./PermissionsSkeleton";
import { handleError } from "../../../stores/components/handleError";
import { useFetchRolesAllQuery } from "../../../stores/api/roles.api";
import {
  useFetchPermissionsMutation,
  useAssignPermissionsMutation,
} from "../../../stores/api/auth.api";
import payloadHelper from "../../../stores/api/helpers/payload.helper";
import { Toggle } from "../../../Components/Form";

const groupFromPermissionsJson = (permissionsJson) => {
  if (!permissionsJson) return [];

  if (typeof permissionsJson === "string") {
    permissionsJson = JSON.parse(permissionsJson);
  }

  if (typeof permissionsJson === "object" && !Array.isArray(permissionsJson)) {
    return Object.entries(permissionsJson).map(([module, permissions]) => ({
      title: module,
      items: Object.entries(permissions).map(([key, value]) => ({
        key,
        label: key.replaceAll("_", " "),
        value,
      })),
    }));
  }

  if (Array.isArray(permissionsJson)) {
    return permissionsJson.map((g) => ({
      title: g.module || "Permissions",
      items: (g.permissions || []).map((p) => ({ key: p, label: p })),
    }));
  }

  return [];
};

const normalizePermissionItem = (item) => {
  if (!item) return null;
  const key = item.key || item.permission || item.name || item.id;
  const label = item.label || item.title || item.name || key;
  const description = item.description || item.desc || item.detail || "";
  const value = item.value !== undefined ? item.value : 0;
  if (!key) return null;
  return { key, label, description, value };
};



const PermissionsForm = () => {
  const { data: rolesRaw = [], isFetching: rolesLoading } = useFetchRolesAllQuery();
  const roles = useMemo(
    () => (rolesRaw || []).map((r) => ({ label: r.name, value: r.id })),
    [rolesRaw]
  );

  const [fetchPermissions, { data: permissions, isLoading: permsLoading }] =
    useFetchPermissionsMutation();
  const [assignPermissions, { isLoading: assigning }] = useAssignPermissionsMutation();

  const loading = rolesLoading || permsLoading || assigning;

  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState({});

  const loadPermissions = async (roleId) => {
    if (!roleId) {
      setSelectedPermissions({});
      return;
    }
    try {
      const res = await fetchPermissions(payloadHelper.forFetchPermissions(roleId)).unwrap();
      const nextState = {};
      for (const [, perms] of Object.entries(res || {})) {
        if (perms && typeof perms === "object") {
          for (const [key, value] of Object.entries(perms)) {
            nextState[key] = Number(value);
          }
        }
      }
      setSelectedPermissions(nextState);
    } catch (err) {
      handleError(err, "Failed to load permissions.");
    }
  };

  const groupedCards = useMemo(() => groupFromPermissionsJson(permissions), [permissions]);

  const togglePermission = (permissionKey) => {
    setSelectedPermissions((prev) => ({
      ...prev,
      [permissionKey]: prev[permissionKey] === 1 ? 0 : 1,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();

    if (!selectedRoleId) {
      ToastMessage({ type: "warning", title: "Validation", message: "Please select a role." });
      return;
    }

    try {
      const groupedByModule = {};
      for (const group of groupedCards || []) {
        const modTitle = group?.title;
        for (const it of group?.items || []) {
          const key = it?.key;
          if (!key) continue;
          if (!groupedByModule[modTitle]) groupedByModule[modTitle] = {};
          groupedByModule[modTitle][key] = selectedPermissions?.[key] === 1 ? 1 : 0;
        }
      }

      await assignPermissions(
        payloadHelper.forAssignPermissions({
          role_id: selectedRoleId,
          permissions: groupedByModule,
        })
      ).unwrap();

      ToastMessage({
        type: "success",
        title: "Permissions Saved",
        message: `Assigned ${Object.values(selectedPermissions).filter((v) => v === 1).length} permission(s) to the selected role.`,
      });
    } catch (err) {
      handleError(err, "Failed to assign permissions.");
    }
  };

  return (
    <div className="bg-white shadow-sm rounded-md dark:bg-slate-800 p-4">
      <div className="flex items-start justify-between gap-4 border-b border-gray-200 pb-3 dark:border-gray-700">
        <div>
          <h2 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-gray-100">Permission Management</h2>
          <p className="text-sm text-gray-500 dark:text-gray-300 mt-1">
            Select a role and toggle permissions grouped by module.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block font-medium mb-1 text-gray-700 dark:text-gray-200">
              Role <span className="text-red-500 dark:text-red-400 ml-1">*</span>
            </label>
            <Select
              options={roles}
              isClearable
              onChange={(e) => {
                const value = e?.value || null;
                setSelectedRoleId(value);
                loadPermissions(value);
              }}
              disabled={loading}
              blurInputOnSelect
              styles={CustomSelectStyle}
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-2 border-gray-200 dark:border-gray-700">
            <Button
              type="button"
              className="!bg-gray-100 !text-gray-700 hover:!bg-gray-200 dark:!bg-slate-800 dark:!text-gray-200"
              onClick={() => setSelectedPermissions({})}
              disabled={loading}
            >
              Clear
            </Button>

            <Button
              type="submit"
              className="!bg-green-500 !text-white hover:!bg-green-600 disabled:!bg-green-300"
              loading={assigning}
              loadingText="Saving..."
              disabled={loading || !selectedRoleId}
            >
              Save Permissions
            </Button>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Permissions</h3>
            <div className="text-xs text-gray-500 dark:text-gray-300">{loading ? "Loading..." : "Grouped modules"}</div>
          </div>

          {permsLoading ? (
            <PermissionsSkeleton />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
              {groupedCards.map((group) => (
                <div key={group.title} className="rounded-md border border-gray-200 dark:border-gray-700 p-3 bg-gradient-to-b from-white to-gray-50 dark:from-slate-800 dark:to-slate-900">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold text-gray-800 dark:text-gray-100 text-sm">{formatToTitle(group.title)}</div>
                  </div>

                  <div className="space-y-2 max-h-[420px] overflow-auto pr-1">
                    {(group.items || []).map((it) => {
                      const item = normalizePermissionItem(it);
                      if (!item) return null;
                      const checked = selectedPermissions[item.key] === 1;
                      const disabled = !selectedRoleId || loading;
                      return (
                        <div
                          key={item.key}
                          className="group flex items-start gap-3 rounded-md border border-transparent hover:border-green-200 dark:hover:border-green-500/20 p-2 transition"
                        >
                          <Toggle
                            checked={checked}
                            disabled={disabled}
                            onChange={() => togglePermission(item.key)}
                          />

                          <div className="flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <div className="text-sm font-medium text-gray-800 dark:text-gray-100">{formatToTitle(item.label)}</div>
                            </div>
                            {item.description ? (
                              <div className="text-xs text-gray-500 dark:text-gray-300 mt-1">{item.description}</div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}

                    {!group.items?.length ? (
                      <div className="text-xs text-gray-500 dark:text-gray-400">No permissions</div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            className="!bg-gray-100 !text-gray-700 hover:!bg-gray-200 dark:!bg-slate-800 dark:!text-gray-200"
            onClick={() => setSelectedPermissions({})}
            disabled={loading}
          >
            Clear
          </Button>

          <Button
            type="submit"
            className="!bg-green-500 !text-white hover:!bg-green-600 disabled:!bg-green-300"
            loading={assigning}
            loadingText="Saving..."
            disabled={loading || !selectedRoleId}
          >
            Save Permissions
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PermissionsForm;
